// src/pages/QuizPage/hooks/useMarafonQuiz.ts
import { useState, useEffect, useCallback, useRef } from "react";
import { marathonAPI } from "../../../api";
import { mapRawQuestions } from "../utils/quizLang";
import { shuffleArray, getShuffleSettings } from "../utils/shuffleUtils";
import type { Question, AnswerResult } from "../utils/quizTypes";

const INITIAL_LIVES = 3;
const INITIAL_BATCH = 5;
const NEXT_QUESTION_DELAY = 100; // ms — savol qo'shilgandan keyin current++
const GAME_OVER_DELAY = 800; // ms — lives=0 dan keyin finished

type UiLang = "oz" | "uz" | "ru";

interface UseMarafonOptions {
  lang: UiLang;
}

export function useMarafonQuiz({ lang }: UseMarafonOptions) {
  const [rawQs, setRawQs] = useState<any[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [current, setCurrent] = useState(0);
  const [answered, setAnswered] = useState<Record<number, number>>({});
  const [answerMeta, setAnswerMeta] = useState<Record<number, AnswerResult>>(
    {},
  );
  const [lives, setLives] = useState(INITIAL_LIVES);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [finished, setFinished] = useState(false);

  // Async race condition'lar uchun
  const pendingNextRef = useRef(false);
  const gameOverScheduledRef = useRef(false);
  // State (answered) asinxron yangilanadi — bir savolga ketma-ket ikki marta
  // (masalan tez ikki bosish) javob berilishining oldini sinxron ref bilan olamiz.
  const answeredQuestionsRef = useRef<Set<number>>(new Set());

  // ─── INIT: 5 ta savol yuklash ──────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        const res = await marathonAPI.getQuestions(lang, INITIAL_BATCH, []);
        if (cancelled) return;
        const raw = res.data?.data || res.data || [];
        const { shuffleQuestions } = getShuffleSettings();
        setRawQs(shuffleQuestions ? shuffleArray(raw) : raw);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.response?.data?.message || "Savollarni yuklab bo'lmadi");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    init();
    return () => {
      cancelled = true;
    };
    // Lang o'zgarganda qayta yuklamaymiz — questions remap qilamiz
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── rawQs → questions mapping (til o'zgarsa ham ishlaydi) ─────────
  useEffect(() => {
    if (!rawQs.length) return;
    setQuestions((prev) => mapRawQuestions(rawQs, lang, answerMeta, prev));
  }, [rawQs, lang, answerMeta]);

  // ─── pendingNext: yangi savol qo'shilgandan keyin current++ ────────
  useEffect(() => {
    if (!pendingNextRef.current) return;
    // questions.length update bo'lgach trigger bo'ladi
    const timer = setTimeout(() => {
      setCurrent((p) => p + 1);
      pendingNextRef.current = false;
    }, NEXT_QUESTION_DELAY);
    return () => clearTimeout(timer);
  }, [questions.length]);

  // ─── ANSWER ─────────────────────────────────────────────────────────
  const handleAnswer = useCallback(
    async (orderIndex: number) => {
      if (answeredQuestionsRef.current.has(current) || finished) return;
      const q = questions[current];
      if (!q) return;
      answeredQuestionsRef.current.add(current);

      const isCorrect = q.correct_answer_index === orderIndex;

      // 1. answered va answerMeta update
      setAnswered((prev) => ({ ...prev, [current]: orderIndex }));
      setAnswerMeta((prev) => ({
        ...prev,
        [q.id]: {
          selectedIndex: orderIndex,
          isCorrect,
          correctAnswerIndex: q.correct_answer_index,
        },
      }));

      if (isCorrect) {
        // 2a. To'g'ri javob → yangi savol yuklash
        try {
          const excludeIds = questions.map((q) => q.id);
          const res = await marathonAPI.getQuestions(lang, 1, excludeIds);
          const nextQ = res.data?.data?.[0] || res.data?.[0];
          if (nextQ) {
            setRawQs((prev) => [...prev, nextQ]);
            pendingNextRef.current = true;
          }
        } catch {
          // Yangi savol kelmasa ham marafon davom etaversin — silent fail
        }
      } else {
        // 2b. Xato javob → lives -1
        setLives((prev) => {
          const next = prev - 1;
          if (next <= 0 && !gameOverScheduledRef.current) {
            gameOverScheduledRef.current = true;
            setTimeout(() => setFinished(true), GAME_OVER_DELAY);
          }
          return next;
        });
      }
    },
    [answered, current, finished, questions, lang],
  );

  // ─── Yakunlash (natija ekranini ko'rsatish) ────────────────────────
  const finish = useCallback(() => {
    setFinished(true);
  }, []);

  // ─── Reset / Restart ───────────────────────────────────────────────
  const reset = useCallback(() => {
    pendingNextRef.current = false;
    gameOverScheduledRef.current = false;
    answeredQuestionsRef.current = new Set();
    setRawQs([]);
    setQuestions([]);
    setCurrent(0);
    setAnswered({});
    setAnswerMeta({});
    setLives(INITIAL_LIVES);
    setFinished(false);
    setError(null);
    setLoading(true);

    // Qayta yuklash
    marathonAPI
      .getQuestions(lang, INITIAL_BATCH, [])
      .then((res) => {
        const raw = res.data?.data || res.data || [];
        const { shuffleQuestions } = getShuffleSettings();
        setRawQs(shuffleQuestions ? shuffleArray(raw) : raw);
      })
      .catch((e: any) => {
        setError(e?.response?.data?.message || "Savollarni yuklab bo'lmadi");
      })
      .finally(() => setLoading(false));
  }, [lang]);

  // ─── Hisoblar ──────────────────────────────────────────────────────
  const correctCount = Object.keys(answered).filter(
    (k) => questions[Number(k)]?.correct_answer_index === answered[Number(k)],
  ).length;

  const wrongCount = Object.keys(answered).filter(
    (k) => questions[Number(k)]?.correct_answer_index !== answered[Number(k)],
  ).length;

  return {
    // State
    questions,
    current,
    answered,
    answerMeta,
    lives,
    loading,
    error,
    finished,
    correctCount,
    wrongCount,
    // Actions
    handleAnswer,
    setCurrent,
    finish,
    reset,
  };
}
